/**
 * DEBRIS, private to bulk-action-bar: the Chakra MenuActions engine, frozen
 * here when everything else moved onto the ComplyKit Menu.
 *
 * The bulk bar's field menu hosts its editors (EditablePopover, a floating-ui
 * popover in a Chakra portal) through `wrapper()`, which clones trigger wiring
 * (ref, onPointerDown, onClick) onto the menu item. The ComplyKit MenuItem's
 * closed interface drops injected props, and React Aria renders wrappers in
 * its hidden collection pass, so the editors can never open from a ComplyKit
 * item (spike-proven). This copy dies when the field editors move onto the
 * ComplyKit Popover/Select rounds, which own the bulk-edit surface redesign.
 *
 * Frozen as-is apart from the default trigger seat, which no seat here used
 * (the bulk bar always passes renderTrigger).
 */
import {
  Box,
  BoxProps,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuItemProps,
  MenuList,
  MenuListProps,
  MenuProps,
  Portal,
  Tooltip,
  TooltipProps,
} from '@chakra-ui/react';
import { Spinner } from '@main/complykit/components/spinner';
import { Text } from '@main/complykit/components/text';
import { isAbortError } from '@main/shared/utils';
import {
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useState,
} from 'react';

import type {
  Action,
  BaseAction,
  IconMenuAction,
  LinkAction,
  WrappedAction,
} from '../actions/types';

/**
 * The Chakra engine's config keeps the event-ful onClick the ComplyKit
 * MenuActionItem dropped: these items render as real Chakra buttons and menu
 * items, so the handlers really receive MouseEvents.
 */
export type ChakraOnClickAction = {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
};

export type ChakraMenuActionItem<T = unknown> = Action & { colorScheme?: 'red' } & IconMenuAction &
  (ChakraOnClickAction | WrappedAction<T> | LinkAction);

export interface TriggerButtonProps extends MenuButtonProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
}

type RenderTriggerProps = TriggerButtonProps & {
  ref?: Ref<HTMLButtonElement>;
};

type CustomMenuButtonProps = TriggerButtonProps & {
  renderTrigger: (props: RenderTriggerProps) => ReactElement;
};

const CustomMenuButton = forwardRef<HTMLButtonElement, CustomMenuButtonProps>(
  ({ renderTrigger, ...props }, ref) => renderTrigger({ ...props, ref }),
);

export interface MenuActionsProps<T = unknown> {
  actions: ChakraMenuActionItem<T>[] | undefined;
  renderTrigger: (props: RenderTriggerProps) => ReactElement;
  closeOnSelect?: boolean;
  closeOnBlur?: boolean;
  isOpen?: boolean;
  extra?: T;
  placement?: MenuProps['placement'];
  menuListProps?: Omit<MenuListProps, 'children'>;
  menuItemsProps?: Omit<BoxProps, 'children'>;
  onOpen?: () => void;
  onClose?: () => void;
}

export function MenuActions<T>({
  actions,
  extra,
  renderTrigger,
  closeOnSelect = true,
  closeOnBlur = true,
  isOpen,
  placement,
  menuListProps,
  menuItemsProps,
  onOpen,
  onClose,
}: MenuActionsProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [pendingActionLabel, setPendingActionLabel] = useState<string>();
  const isControlled = isOpen !== undefined;
  const isMenuOpen = isControlled ? isOpen : internalOpen;

  const setMenuOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      if (nextOpen) {
        onOpen?.();
        return;
      }

      onClose?.();
    },
    [isControlled, onClose, onOpen],
  );

  if (!actions?.length) {
    return null;
  }

  const actionItems = actions.map((action) => {
    const button = (
      <MenuActionButton
        key={action.label}
        action={action}
        isLoading={action.isLoading || pendingActionLabel === action.label}
        onActionPendingChange={setPendingActionLabel}
        onOpenChange={setMenuOpen}
      />
    );

    if ('wrapper' in action && typeof action.wrapper === 'function') {
      return action.wrapper(button, extra as T);
    }

    return (
      <RenderAction
        key={action.label}
        action={action}
        extra={extra}
        tooltipProps={{ placement: 'left' }}
      >
        {button}
      </RenderAction>
    );
  });

  return (
    <Menu
      computePositionOnMount
      strategy="fixed"
      isLazy
      closeOnSelect={closeOnSelect}
      closeOnBlur={closeOnBlur}
      isOpen={isMenuOpen}
      placement={placement}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
    >
      <MenuButton as={CustomMenuButton} renderTrigger={renderTrigger} />
      <Portal>
        <MenuList
          {...menuListProps}
          ref={(el) => {
            if (el) {
              const patchedFocus = () => {
                /* Monkey patch the focus to prevent from focus being stolen which then closes nested popovers such as with bulk actions */
                /* This focus behaviour is hard coded in Chakra UI and this was the only way we could find to override it */
              };

              try {
                el.focus = patchedFocus;
              } catch {
                Object.defineProperty(el, 'focus', {
                  value: patchedFocus,
                  configurable: true,
                });
              }
            }
          }}
        >
          {menuItemsProps ? (
            <Box role="none" {...menuItemsProps}>
              {actionItems}
            </Box>
          ) : (
            actionItems
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}

type MenuActionButtonProps = Omit<MenuItemProps, 'icon'> & {
  action: ChakraMenuActionItem;
  isLoading: boolean;
  onActionPendingChange: (label?: string) => void;
  onOpenChange?: (open: boolean) => void;
};

const MenuActionButton = forwardRef<HTMLButtonElement, MenuActionButtonProps>(
  ({ action, isLoading, onActionPendingChange, onOpenChange, onClick, ...menuItemProps }, ref) => {
    const handleClick =
      'onClick' in action
        ? (event: MouseEvent<HTMLButtonElement>) => {
            if (action.closeOnSelect === false) {
              onOpenChange?.(true);
            }

            const result = action.onClick(event);

            if (!isPromiseLike(result)) {
              return;
            }

            onActionPendingChange(action.label);

            void result
              .finally(() => {
                onActionPendingChange(undefined);

                if (action.closeOnSelect === false) {
                  onOpenChange?.(false);
                }
              })
              .catch((error) => {
                if (!isAbortError(error)) {
                  throw error;
                }
              });
          }
        : undefined;

    return (
      <MenuItem
        {...menuItemProps}
        ref={ref}
        fontSize="sm"
        lineHeight={5}
        iconSpacing={2}
        icon={<Icon boxSize={4} as={action.icon} />}
        onClick={(event) => {
          onClick?.(event);
          handleClick?.(event);
        }}
        as={'href' in action ? 'a' : undefined}
        href={'href' in action ? action.href : undefined}
        target={'href' in action && action.isExternal ? '_blank' : undefined}
        closeOnSelect={action.closeOnSelect}
        isDisabled={action.isDisabled || isLoading}
        color={action.colorScheme ? `${action.colorScheme}.600` : undefined}
      >
        <Flex align="center" justify="space-between" w="full" gap={3}>
          <Text size="sm" isGrow>
            {action.label}
          </Text>
          <Box display="flex" visibility={isLoading ? 'visible' : 'hidden'} flexShrink={0}>
            <Spinner size="sm" />
          </Box>
        </Flex>
      </MenuItem>
    );
  },
);

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  );
}

export function RenderAction<T>({
  action,
  extra,
  tooltipProps,
  children,
}: PropsWithChildren<{
  action: ChakraMenuActionItem<T>;
  extra?: unknown;
  tooltipProps?: Omit<TooltipProps, 'children'>;
}>) {
  const isDisabled =
    action.isDisabled !== undefined && !action.tooltip ? !action.isDisabled : false;

  return (
    <RenderWrappedAction key={action.label} action={action} extra={extra}>
      {isDisabled ? (
        children
      ) : (
        <Tooltip
          isDisabled={isDisabled}
          label={
            action.isDisabled && action.disabledTooltip ? action.disabledTooltip : action.tooltip
          }
          hasArrow
          {...tooltipProps}
        >
          {children}
        </Tooltip>
      )}
    </RenderWrappedAction>
  );
}

function RenderWrappedAction<T = unknown>({
  action,
  extra,
  children,
}: {
  action: BaseAction & Partial<WrappedAction<T>>;
  extra: T;
  children: ReactNode;
}) {
  return action.wrapper?.(children as ReactElement, extra) ?? children;
}
